import "./ProfileSettingsPane.css";
import "./settings-ui.css";
import { useEffect, useMemo, useState } from "preact/hooks";
import { DestructiveActionButton } from "../../components/DestructiveActionButton/DestructiveActionButton.jsx";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { StatusMessage } from "../../components/StatusMessage/StatusMessage.jsx";
import { localizeErrorMessage } from "../../lib/localizeErrorMessage.js";
import {
  archiveProfile,
  createProfile,
  getActiveBudgetScope,
  listProfileYears,
  listProfiles,
  renameProfile,
  setActiveBudgetScope
} from "../../lib/storage/walletWatchDb.js";

const profilePaneLayout = createDashboardLayout({
  columns: [
    { size: "fill" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["profiles", "details"]
  ]
});

const profileDetailsLayout = createDashboardLayout({
  columns: [
    { size: "fill" }
  ],
  rows: [
    { size: "fixed", value: "auto" },
    { size: "fill" }
  ],
  areas: [
    ["current"],
    ["create"]
  ]
});

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

function renderStatusFooter(status, fallback) {
  return status ? (
    <StatusMessage type={status.type} message={status.message} />
  ) : (
    fallback
  );
}

export function ProfileSettingsPane({ onScopeChanged, t = (key) => key }) {
  const [profiles, setProfiles] = useState([]);
  const [activeScope, setActiveScopeState] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [profilesStatus, setProfilesStatus] = useState(null);
  const [currentProfileStatus, setCurrentProfileStatus] = useState(null);
  const [createProfileStatus, setCreateProfileStatus] = useState(null);

  const activeProfile = useMemo(() => (
    profiles.find((profile) => profile.id === activeScope?.profileId) ?? null
  ), [activeScope?.profileId, profiles]);
  const orderedProfiles = useMemo(() => {
    const activeProfileId = activeScope?.profileId;

    return profiles.slice().sort((left, right) => {
      if (left.id === activeProfileId && right.id !== activeProfileId) {
        return -1;
      }

      if (right.id === activeProfileId && left.id !== activeProfileId) {
        return 1;
      }

      return 0;
    });
  }, [activeScope?.profileId, profiles]);

  async function loadProfileState(scopeOverride = null) {
    const [scope, nextProfiles] = await Promise.all([
      scopeOverride ?? getActiveBudgetScope(),
      listProfiles()
    ]);

    setProfiles(nextProfiles);
    setActiveScopeState(scope);
    const active = nextProfiles.find((profile) => profile.id === scope.profileId);
    setRenameValue(active?.name ?? "");
  }

  useEffect(() => {
    loadProfileState().catch((error) => {
      console.error("Failed to load profile settings state.", error);
    });
  }, []);

  function setProfilesPanelStatus(nextStatus) {
    setCurrentProfileStatus(null);
    setCreateProfileStatus(null);
    setProfilesStatus(nextStatus);
  }

  function setCurrentProfilePanelStatus(nextStatus) {
    setProfilesStatus(null);
    setCreateProfileStatus(null);
    setCurrentProfileStatus(nextStatus);
  }

  function setCreateProfilePanelStatus(nextStatus) {
    setProfilesStatus(null);
    setCurrentProfileStatus(null);
    setCreateProfileStatus(nextStatus);
  }

  async function handleProfileSwitch(profileId) {
    if (!activeScope || profileId === activeScope.profileId) {
      return;
    }

    try {
      const years = await listProfileYears(profileId);
      const nextYear = years[0]?.year;
      if (!nextYear) {
        throw new Error("The selected profile has no available year.");
      }

      const nextScope = await setActiveBudgetScope(profileId, nextYear);
      await loadProfileState(nextScope);
      await onScopeChanged?.(nextScope);
      setProfilesPanelStatus({
        type: "success",
        message: `${t("messages.profileSettings.switchedTo")} ${profiles.find((profile) => profile.id === profileId)?.name ?? t("messages.profileSettings.selectedProfile")}.`
      });
    } catch (error) {
      setProfilesPanelStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.profileSettings.failedToSwitchProfile")
      });
    }
  }

  async function handleRenameProfile() {
    if (!activeProfile) {
      return;
    }

    try {
      await renameProfile(activeProfile.id, renameValue);
      await loadProfileState(activeScope);
      setCurrentProfilePanelStatus({
        type: "success",
        message: t("messages.profileSettings.updatedCurrentProfileName")
      });
    } catch (error) {
      setCurrentProfilePanelStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.profileSettings.failedToRenameCurrentProfile")
      });
    }
  }

  async function handleCreateProfile() {
    try {
      const created = await createProfile(newProfileName, {
        year: activeScope?.year
      });
      setNewProfileName("");
      await loadProfileState(activeScope);
      setCreateProfilePanelStatus({
        type: "success",
        message: `${t("messages.profileSettings.created")} ${created.profile.name}.`
      });
    } catch (error) {
      setCreateProfilePanelStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.profileSettings.failedToCreateNewProfile")
      });
    }
  }

  async function handleDeleteProfile(profileId) {
    try {
      const result = await archiveProfile(profileId);

      if (result.nextScope) {
        await onScopeChanged?.(result.nextScope);
      }

      await loadProfileState(result.nextScope ?? activeScope);
      setProfilesPanelStatus({
        type: "success",
        message: `${t("messages.profileSettings.deleted")} ${result.deletedProfile.name}.`
      });
    } catch (error) {
      setProfilesPanelStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.profileSettings.failedToDeleteProfile")
      });
    }
  }

  function handleRenameSubmit(event) {
    event.preventDefault();
    handleRenameProfile();
  }

  function handleCreateSubmit(event) {
    event.preventDefault();
    handleCreateProfile();
  }

  return (
    <DashboardPage
      layout={profilePaneLayout}
      slots={[
        {
          key: "profiles",
          area: "profiles",
          content: (
            <Panel
              padded
              className="settings-pane--compact"
              scroll
              header={t("profiles")}
              footerClassName={footerToneClass(profilesStatus?.type)}
              footer={renderStatusFooter(
                profilesStatus,
                t("messages.profileSettings.useSeparateProfilesOnlyForTrulySeparateDatasets")
              )}
            >
              <div class="profile-settings-pane settings-form-stack">
                <div class="settings-item-list">
                  {orderedProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      class={`settings-record${profile.id === activeScope?.profileId ? " active" : ""}`}
                    >
                      <div class="settings-record-body">
                        <span class="settings-item-label">{profile.name}</span>
                        <span class="settings-item-meta">
                          {profile.id === activeScope?.profileId ? t("current") : profile.source}
                        </span>
                      </div>
                      {profile.id !== activeScope?.profileId ? (
                        <div class="settings-record-actions">
                          <button
                            class="settings-action-button"
                            type="button"
                            onClick={() => handleProfileSwitch(profile.id)}
                          >
                            {t("switch")}
                          </button>
                          {profiles.length > 1 ? (
                            <DestructiveActionButton
                              className="profile-settings-delete"
                              onConfirm={() => handleDeleteProfile(profile.id)}
                            >
                              {t("delete")}
                            </DestructiveActionButton>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          )
        },
        {
          key: "details",
          area: "details",
          content: (
            <DashboardPage
              layout={profileDetailsLayout}
              slots={[
                {
                  key: "current",
                  area: "current",
                  fill: false,
                  content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      scroll
                      header={t("currentProfile")}
                      footerClassName={footerToneClass(currentProfileStatus?.type)}
                      footer={renderStatusFooter(
                        currentProfileStatus,
                        activeScope
                          ? `${t("currentProfile")}: ${activeProfile?.name ?? t("profile")}`
                          : t("messages.profileSettings.loadingState")
                      )}
                    >
                      <div class="profile-settings-pane settings-form-stack">
                        <form class="settings-field-row profile-settings-rename-row" onSubmit={handleRenameSubmit}>
                          <label class="settings-field">
                            <span class="settings-field-label">{t("name")}</span>
                            <input
                              class="settings-text-input"
                              type="text"
                              value={renameValue}
                              onInput={(event) => setRenameValue(event.currentTarget.value)}
                            />
                          </label>
                          <button
                            class="settings-action-button primary"
                            type="submit"
                          >
                            {t("saveName")}
                          </button>
                        </form>
                      </div>
                    </Panel>
                  )
                },
                {
                  key: "create",
                  area: "create",
                  content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      scroll
                      header={t("createProfile")}
                      footerClassName={footerToneClass(createProfileStatus?.type)}
                      footer={renderStatusFooter(
                        createProfileStatus,
                        t("messages.profileSettings.createNewLocalProfile")
                      )}
                    >
                      <div class="profile-settings-pane settings-form-stack">
                        <form class="settings-field-row profile-settings-create-row" onSubmit={handleCreateSubmit}>
                          <label class="settings-field">
                            <span class="settings-field-label">{t("name")}</span>
                            <input
                              class="settings-text-input"
                              type="text"
                              placeholder={t("profileName")}
                              value={newProfileName}
                              onInput={(event) => setNewProfileName(event.currentTarget.value)}
                            />
                          </label>
                          <button
                            class="settings-action-button primary"
                            type="submit"
                          >
                            {t("createProfile")}
                          </button>
                        </form>
                      </div>
                    </Panel>
                  )
                }
              ]}
            />
          )
        }
      ]}
    />
  );
}
